import React from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';

import { LomavisBadge1 } from '@/lomavisSharedUi/atoms/badges/LomavisBadge1';
import {
  LomavisIssueListAlert1,
  LomavisIssueListAlertItem1
} from '@/lomavisSharedUi/atoms/feedback/LomavisIssueListAlert1';
import { LomavisCampaignText1 } from '@/lomavisSharedUi/atoms/typography/LomavisCampaignText1';
import { LomavisTypeaheadSelectInput1 } from '@/lomavisSharedUi/atoms/inputs/LomavisTypeaheadSelectInput1';
import { LomavisTabSwitcherCard1 } from '@/lomavisSharedUi/atoms/tabs/LomavisTabSwitcherCard1';
import { LomavisMoreInfo } from '@/lomavisSharedUi/atoms/tooltips/LomavisMoreInfo';
import { LomavisBadgeList1 } from '@/lomavisSharedUi/molecules/badges/LomavisBadgeList1';
import { LomavisCampaignLivePreview1 } from '@/lomavisSharedUi/molecules/cards/LomavisCampaignLivePreview1';
import {
  LomavisBidStrategy1,
  LomavisBudgetControls1,
  LomavisBudgetType1
} from '@/lomavisSharedUi/molecules/inputs/LomavisBudgetControls1';
import {
  LomavisMediaUploadFile1,
  LomavisMediaUploadNotice1,
  LomavisMediaUploadPanel1,
  LomavisMediaUploadProgress1
} from '@/lomavisSharedUi/molecules/inputs/LomavisMediaUploadPanel1';
import { LomavisCountryOption } from '@/lomavisSharedUi/molecules/inputs/LomavisCountryTypeahead';
import { LomavisRadiusSelector1 } from '@/lomavisSharedUi/molecules/inputs/LomavisRadiusSelector1';
import {
  LomavisPlacementOption1,
  LomavisPlacementPlatform1
} from '@/lomavisSharedUi/molecules/segments/LomavisPlacementOption1';
import { LomavisCampaignSectionHeader1 } from '@/lomavisSharedUi/molecules/typography/LomavisCampaignSectionHeader1';

import {
  LomavisCampaignActionDropdown1,
  LomavisCampaignActionDropdownItem1
} from './LomavisCampaignActionDropdown1';
import { LomavisCampaignPrimaryTextEditor1 } from './LomavisCampaignPrimaryTextEditor1';
import { getCampaignPlainTextFromRichText } from './richText';
import styles from './LomavisCampaignBuilder1.module.css';

export { getCampaignPlainTextFromRichText } from './richText';
export { getCampaignReviewTableColumns } from './campaignReviewTable';
export type { LomavisCampaignReviewRow1 } from './campaignReviewTable';

const DSA_ENTITY_MAX_LENGTH = 512;
const restrictedSpecialAdCategoryIds = new Set([
  'HOUSING',
  'EMPLOYMENT',
  'FINANCIAL_PRODUCTS_SERVICES'
]);

export type LomavisCampaignBuilderTabKey1 =
  | 'campaign'
  | 'targeting'
  | 'creative'
  | 'budget';

export type LomavisCampaignBuilderOption1 = {
  id: string | number;
  name: string;
  raw_data?: any;
};

export type LomavisCampaignBuilderSelectOption1 = {
  value: string;
  label: string;
};

export type LomavisCampaignBuilderLocation1 = LomavisCampaignBuilderOption1 & {
  id: string;
  coordinate?: { lat: number; lng: number };
  radiusKm: number;
  raw_data?: any;
};

export type LomavisCampaignBuilderPlacement1 = {
  id: string;
  label: string;
  platform: LomavisPlacementPlatform1;
  selected: boolean;
};

export type LomavisCampaignBuilderAd1 = {
  accountName: string;
  profileImageUrl?: string;
  sponsoredLabel: string;
  primaryText: string;
  landingUrl: string;
  cta: string;
};

export interface LomavisCampaignBuilder1Props {
  campaignName: string;
  onCampaignNameChange?: (campaignName: string) => void;
  audienceEstimate: React.ReactNode;
  activeTabKey: LomavisCampaignBuilderTabKey1;
  onTabChange: (tabKey: LomavisCampaignBuilderTabKey1) => void;
  placements: LomavisCampaignBuilderPlacement1[];
  onPlacementToggle: (id: string) => void;
  objective: string;
  onObjectiveChange: (objective: string) => void;
  objectiveOptions?: LomavisCampaignBuilderSelectOption1[];
  locationOptions: LomavisCampaignBuilderLocation1[];
  locations: LomavisCampaignBuilderLocation1[];
  onLocationsChange: (locations: LomavisCampaignBuilderLocation1[]) => void;
  onLocationSearch?: (
    query: string,
    setLoading: (loading: boolean) => void
  ) => void;
  targetCountryOptions?: LomavisCampaignBuilderOption1[];
  targetCountries?: LomavisCampaignBuilderOption1[];
  onTargetCountriesChange?: (
    targetCountries: LomavisCampaignBuilderOption1[]
  ) => void;
  onTargetCountrySearch?: (
    query: string,
    setLoading: (loading: boolean) => void
  ) => void;
  radiusKm: number;
  radiusMinKm?: number;
  radiusMaxKm?: number;
  onRadiusChange: (radiusKm: number) => void;
  ageRange: [number, number];
  onAgeRangeChange: (ageRange: [number, number]) => void;
  gender: string;
  onGenderChange: (gender: string) => void;
  genderOptions?: LomavisCampaignBuilderSelectOption1[];
  languageOptions: LomavisCampaignBuilderOption1[];
  languages: LomavisCampaignBuilderOption1[];
  onLanguagesChange: (languages: LomavisCampaignBuilderOption1[]) => void;
  onLanguageSearch?: (
    query: string,
    setLoading: (loading: boolean) => void
  ) => void;
  interestOptions: LomavisCampaignBuilderOption1[];
  interests: LomavisCampaignBuilderOption1[];
  onInterestsChange: (interests: LomavisCampaignBuilderOption1[]) => void;
  onInterestSearch?: (
    query: string,
    setLoading: (loading: boolean) => void
  ) => void;
  behaviors: LomavisCampaignBuilderOption1[];
  onBehaviorRemove: (id: string) => void;
  interestsSectionSlot?: React.ReactNode;
  customAudiences?: LomavisCampaignBuilderOption1[];
  onCustomAudiencesChange?: (
    customAudiences: LomavisCampaignBuilderOption1[]
  ) => void;
  customAudienceOptions?: LomavisCampaignBuilderOption1[];
  isLoadingCustomAudiences?: boolean;
  customAudiencesError?: string | null;
  onRetryCustomAudiences?: () => void;
  specialAdCategoryOptions?: LomavisCampaignBuilderSelectOption1[];
  specialAdCategories?: string[];
  onSpecialAdCategoriesChange?: (specialAdCategories: string[]) => void;
  countryOptions?: LomavisCountryOption[];
  specialAdCategoryCountry?: string[];
  onSpecialAdCategoryCountryChange?: (specialAdCategoryCountry: string[]) => void;
  pixel?: LomavisCampaignBuilderOption1 | null;
  pixelOptions?: LomavisCampaignBuilderOption1[];
  isLoadingPixels?: boolean;
  onPixelChange: (pixel: LomavisCampaignBuilderOption1 | null) => void;
  targetEvent?: LomavisCampaignBuilderOption1 | null;
  onTargetEventChange: (
    targetEvent: LomavisCampaignBuilderOption1 | null
  ) => void;
  targetEventOptions?: LomavisCampaignBuilderOption1[];
  ad: LomavisCampaignBuilderAd1;
  onAdChange: (ad: LomavisCampaignBuilderAd1) => void;
  ctaOptions?: LomavisCampaignBuilderSelectOption1[];
  selectedMediaFiles: LomavisMediaUploadFile1[];
  onFileRemove: (id: string) => void;
  activePreviewPlacementKey: string;
  onPreviewPlacementChange: (key: string) => void;
  onBrowseMedia: () => void;
  onOpenMediaLibrary?: () => void;
  onMediaFilesDrop: (files: File[]) => void;
  onCancelUpload?: () => void;
  uploadProgress?: LomavisMediaUploadProgress1 | null;
  mediaValidationNotice?: LomavisMediaUploadNotice1 | null;
  mediaUploadDisabled?: boolean;
  budgetType: LomavisBudgetType1;
  onBudgetTypeChange: (budgetType: LomavisBudgetType1) => void;
  amount: string;
  onAmountChange: (amount: string) => void;
  startDate: string;
  onStartDateChange: (startDate: string) => void;
  endDate: string;
  onEndDateChange: (endDate: string) => void;
  bidStrategy: LomavisBidStrategy1;
  onBidStrategyChange: (bidStrategy: LomavisBidStrategy1) => void;
  maxBidAmount: string;
  onMaxBidAmountChange: (amount: string) => void;
  currency?: string;
  dsaBeneficiary: string;
  onDsaBeneficiaryChange: (beneficiary: string) => void;
  dsaPayor: string;
  onDsaPayorChange: (payor: string) => void;
  estimatedTotalLabel: string;
  estimatedPeriodLabel: string;
  isSaving?: boolean;
  isPublishing?: boolean;
  isUploadingMedia?: boolean;
  secondaryActionLabel?: string;
  secondaryActionDisabled?: boolean;
  onSecondaryAction?: () => void;
  saveActionLabel?: string;
  saveActionDisabled?: boolean;
  onSave?: () => void;
  saveAsDraftActionLabel?: string;
  onSaveAsDraft?: () => void;
  cancelActionLabel?: string;
  cancelActionDisabled?: boolean;
  onCancel?: () => void;
  cloneActionLabel?: string;
  cloneActionDisabled?: boolean;
  onClone?: () => void;
  showOnlyCloneAction?: boolean;
  primaryActionLabel?: string;
  primaryActionDisabled?: boolean;
  hidePrimaryAction?: boolean;
  actionValidationIssues?: LomavisIssueListAlertItem1[];
  actionValidationMessages?: string[];
  actionFooterRef?: React.Ref<HTMLDivElement>;
  onPublish: () => void;
  tabsRightSlot?: React.ReactNode;
}

const sectionIds: Record<LomavisCampaignBuilderTabKey1, string> = {
  campaign: 'lomavis-campaign-builder-campaign',
  targeting: 'lomavis-campaign-builder-targeting',
  creative: 'lomavis-campaign-builder-creative',
  budget: 'lomavis-campaign-builder-budget'
};

const tabs = [
  { key: 'campaign', label: 'Campaign', iconClass: 'fa-regular fa-bullhorn' },
  { key: 'targeting', label: 'Targeting', iconClass: 'fa-regular fa-bullseye' },
  { key: 'creative', label: 'Creative', iconClass: 'fa-regular fa-palette' },
  { key: 'budget', label: 'Budget', iconClass: 'fa-regular fa-wallet' }
];

const defaultObjectiveOptions: LomavisCampaignBuilderSelectOption1[] = [
  { value: 'OUTCOME_AWARENESS', label: 'Awareness' },
  { value: 'OUTCOME_TRAFFIC', label: 'Traffic' },
  { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement' },
  { value: 'OUTCOME_LEADS', label: 'Leads' },
  { value: 'OUTCOME_SALES', label: 'Sales' }
];

const defaultGenderOptions: LomavisCampaignBuilderSelectOption1[] = [
  { value: 'all', label: 'All' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' }
];

const defaultSpecialAdCategoryOptions: LomavisCampaignBuilderSelectOption1[] = [
  { value: 'NONE', label: 'None' },
  { value: 'HOUSING', label: 'Housing' },
  { value: 'EMPLOYMENT', label: 'Employment' },
  {
    value: 'FINANCIAL_PRODUCTS_SERVICES',
    label: 'Financial products and services'
  },
  {
    value: 'ISSUES_ELECTIONS_POLITICS',
    label: 'Issues, elections, and politics'
  }
];

const defaultCtaOptions: LomavisCampaignBuilderSelectOption1[] = [
  { value: 'shop_now', label: 'Shop Now' },
  { value: 'learn_more', label: 'Learn More' },
  { value: 'sign_up', label: 'Sign Up' },
  { value: 'get_offer', label: 'Get Offer' }
];

const pixelTrackingObjectives = ['OUTCOME_LEADS', 'OUTCOME_SALES'];
const conversionTrackingObjectives = ['OUTCOME_LEADS', 'OUTCOME_SALES'];

const defaultTargetEventOptions: LomavisCampaignBuilderOption1[] = [
  { id: 'PURCHASE', name: 'Purchase' },
  { id: 'LEAD', name: 'Lead' },
  { id: 'COMPLETE_REGISTRATION', name: 'Complete registration' },
  { id: 'ADD_TO_CART', name: 'Add to cart' },
  { id: 'INITIATED_CHECKOUT', name: 'Initiate checkout' },
  { id: 'CONTENT_VIEW', name: 'View content' },
  { id: 'CONTACT', name: 'Contact' }
];

const toBadgeItems = (items: LomavisCampaignBuilderOption1[]) =>
  items.map((item) => ({
    id: String(item.id),
    title: item.name,
    variant: 'primary'
  }));

const LomavisCampaignBuilderSectionCard1: React.FC<{
  id?: string;
  title: string;
  description: string;
  iconClass?: string;
  children: React.ReactNode;
}> = ({ id, title, description, iconClass, children }) => (
  <section id={id} className={`${styles.section} bg-light`}>
    <div className="container-xxl px-4 py-4">
      <div className="rounded-4 border bg-white">
        <div className="border-bottom px-3 py-3 px-md-4">
          <LomavisCampaignSectionHeader1
            title={title}
            description={description}
            iconClass={iconClass}
          />
        </div>
        <div className="p-3 p-md-4">{children}</div>
      </div>
    </div>
  </section>
);

export const LomavisCampaignBuilder1: React.FC<
  LomavisCampaignBuilder1Props
> = ({
  campaignName,
  onCampaignNameChange,
  audienceEstimate,
  activeTabKey,
  onTabChange,
  placements,
  onPlacementToggle,
  objective,
  onObjectiveChange,
  objectiveOptions = defaultObjectiveOptions,
  locationOptions,
  locations,
  onLocationsChange,
  onLocationSearch,
  targetCountryOptions = [],
  targetCountries = [],
  onTargetCountriesChange,
  onTargetCountrySearch,
  radiusKm,
  radiusMinKm = 5,
  radiusMaxKm = 100,
  onRadiusChange,
  ageRange,
  onAgeRangeChange,
  gender,
  onGenderChange,
  genderOptions = defaultGenderOptions,
  languageOptions,
  languages,
  onLanguagesChange,
  onLanguageSearch,
  interestOptions,
  interests,
  onInterestsChange,
  onInterestSearch,
  behaviors,
  onBehaviorRemove,
  interestsSectionSlot,
  customAudiences = [],
  onCustomAudiencesChange,
  customAudienceOptions = [],
  isLoadingCustomAudiences = false,
  customAudiencesError = null,
  onRetryCustomAudiences,
  specialAdCategoryOptions = defaultSpecialAdCategoryOptions,
  specialAdCategories = [],
  onSpecialAdCategoriesChange,
  countryOptions = [],
  specialAdCategoryCountry = [],
  onSpecialAdCategoryCountryChange,
  pixel = null,
  pixelOptions = [],
  isLoadingPixels = false,
  onPixelChange,
  targetEvent = null,
  onTargetEventChange,
  targetEventOptions = defaultTargetEventOptions,
  ad,
  onAdChange,
  ctaOptions = defaultCtaOptions,
  selectedMediaFiles,
  onFileRemove,
  activePreviewPlacementKey,
  onPreviewPlacementChange,
  onBrowseMedia,
  onOpenMediaLibrary,
  onMediaFilesDrop,
  onCancelUpload,
  uploadProgress = null,
  mediaValidationNotice = null,
  mediaUploadDisabled = false,
  budgetType,
  onBudgetTypeChange,
  amount,
  onAmountChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  bidStrategy,
  onBidStrategyChange,
  maxBidAmount,
  onMaxBidAmountChange,
  currency,
  dsaBeneficiary,
  onDsaBeneficiaryChange,
  dsaPayor,
  onDsaPayorChange,
  estimatedTotalLabel,
  estimatedPeriodLabel,
  isSaving = false,
  isPublishing = false,
  isUploadingMedia = false,
  secondaryActionLabel,
  secondaryActionDisabled = false,
  onSecondaryAction,
  saveActionLabel,
  saveActionDisabled,
  onSave,
  saveAsDraftActionLabel,
  onSaveAsDraft,
  cancelActionLabel,
  cancelActionDisabled = false,
  onCancel,
  cloneActionLabel,
  cloneActionDisabled = false,
  onClone,
  showOnlyCloneAction = false,
  primaryActionLabel,
  primaryActionDisabled = false,
  hidePrimaryAction = false,
  actionValidationIssues = [],
  actionValidationMessages = [],
  actionFooterRef,
  onPublish,
  tabsRightSlot
}) => {
  const { t } = useTranslate();
  const selectedPlacements = placements.filter(
    (placement) => placement.selected
  );
  const specialAdCategoryValue = specialAdCategories[0] || 'NONE';
  const hasSpecialAdCategory = specialAdCategoryValue !== 'NONE';
  const hasRestrictedSpecialAdCategory = restrictedSpecialAdCategoryIds.has(
    specialAdCategoryValue
  );
  const showPixelTracking = pixelTrackingObjectives.includes(objective);
  const showConversionTracking =
    conversionTrackingObjectives.includes(objective);
  const getPlacementLabel = (placement: LomavisCampaignBuilderPlacement1) =>
    t(`adManager.placements.${placement.id}`, placement.label);
  const translatedTabs = tabs.map((tab) => ({
    ...tab,
    label: t(`adManager.tabs.${tab.key}`, tab.label)
  }));
  const resolvedObjectiveOptions = objectiveOptions.map((option) => ({
    ...option,
    label: t(`adManager.objectives.${option.value}`, option.label)
  }));
  const resolvedGenderOptions = genderOptions.map((option) => ({
    ...option,
    label: t(`adManager.gender.${option.value}`, option.label)
  }));
  const resolvedSpecialAdCategoryOptions = specialAdCategoryOptions.map(
    (option) => ({
      ...option,
      label: t(`adManager.specialAdCategories.${option.value}`, option.label)
    })
  );
  const resolvedCtaOptions = ctaOptions.map((option) => ({
    ...option,
    label: t(`adManager.cta.${option.value}`, option.label)
  }));
  const resolvedTargetEventOptions = targetEventOptions.map((option) => ({
    ...option,
    name: t(`adManager.targetEvents.${option.id}`, option.name)
  }));
  const translatedPlacements = placements.map((placement) => ({
    ...placement,
    label: getPlacementLabel(placement)
  }));
  const translatedSelectedPlacements = selectedPlacements.map((placement) => ({
    ...placement,
    label: getPlacementLabel(placement)
  }));
  const previewPlacements = (
    translatedSelectedPlacements.length
      ? translatedSelectedPlacements
      : translatedPlacements
  ).map((placement) => ({
    key: placement.id,
    label: placement.label
  }));
  const activeCtaLabel =
    resolvedCtaOptions.find((option) => option.value === ad.cta)?.label ??
    ad.cta;
  const previewAd = {
    ...ad,
    primaryText: getCampaignPlainTextFromRichText(ad.primaryText),
    cta: activeCtaLabel
  };
  const handleSave = onSave || onSecondaryAction;
  const resolvedSaveLabel =
    saveActionLabel || secondaryActionLabel || t('adManager.save', 'Save');
  const resolvedSaveDisabled =
    saveActionDisabled ?? secondaryActionDisabled ?? false;
  const resolvedSaveAsDraftActionLabel =
    saveAsDraftActionLabel || t('adManager.saveAsDraft', 'Save as draft');
  const resolvedCancelActionLabel =
    cancelActionLabel || t('common.cancel', 'Cancel');
  const resolvedCloneActionLabel =
    cloneActionLabel || t('adManager.cloneCampaign', 'Clone Campaign');
  const resolvedPrimaryActionLabel =
    primaryActionLabel || t('adManager.publishCampaign', 'Publish campaign');
  const dsaBeneficiaryLengthHelpId = 'ad-manager-dsa-beneficiary-length-help';
  const dsaPayorLengthHelpId = 'ad-manager-dsa-payor-length-help';
  const isActionBusy = isSaving || isPublishing;
  const resolvedActionValidationIssues =
    actionValidationIssues.length > 0
      ? actionValidationIssues
      : actionValidationMessages.map((message) => ({
          id: message,
          message
        }));
  // On the create route there's no "Save" (persist-in-place) action, so
  // Save-as-draft is the only save affordance — surface it as a standalone
  // button next to Publish instead of hiding it in the dropdown menu.
  const showStandaloneSaveAsDraft = !handleSave && Boolean(onSaveAsDraft);
  const tailActions: LomavisCampaignActionDropdownItem1[] = [
    ...(onSaveAsDraft && !showStandaloneSaveAsDraft
      ? [
          {
            key: 'save-as-draft',
            label: resolvedSaveAsDraftActionLabel,
            onClick: onSaveAsDraft
          }
        ]
      : []),
    ...(onClone
      ? [
          {
            disabled: cloneActionDisabled,
            key: 'clone',
            label: resolvedCloneActionLabel,
            onClick: onClone
          }
        ]
      : [])
  ];
  const selectedPixelOption = pixel
    ? pixelOptions.find((option) => String(option.id) === String(pixel.id)) ||
      pixel
    : null;
  const handleTabChange = (key: string) => {
    const tabKey = key as LomavisCampaignBuilderTabKey1;
    onTabChange(tabKey);

    if (typeof document !== 'undefined') {
      document
        .getElementById(sectionIds[tabKey])
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-vh-100 bg-light text-body">
      <div className={styles.stickyHeader}>
        <header className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end gap-4 border-bottom bg-white p-4">
          <div>
            <h1 className="fs-2 fw-bold text-gray-900 lh-sm mb-1">
              {t('adManager.builder.title', 'Campaign builder')}
            </h1>
            <LomavisCampaignText1 variant="helper">
              {t(
                'adManager.builder.subtitle',
                'Spring launch setup across paid social placements.'
              )}
            </LomavisCampaignText1>
          </div>
          <div className="text-lg-end">
            <LomavisCampaignText1 variant="eyebrow">
              {t('adManager.audience.estimatedAudience', 'Estimated audience')}
            </LomavisCampaignText1>
            <div className="display-6 fw-bold lh-1">{audienceEstimate}</div>
            <LomavisCampaignText1 variant="helper">
              {t('adManager.people', 'people')}
            </LomavisCampaignText1>
          </div>
        </header>

        <div
          className={`${styles.tabs} d-flex align-items-center justify-content-between gap-3 flex-wrap`}
        >
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <LomavisTabSwitcherCard1
              tabs={translatedTabs}
              activeKey={activeTabKey}
              onChange={handleTabChange}
              accentColor="#0051fe"
              className={styles.tabSwitcher}
            />
          </div>
          {tabsRightSlot ? (
            <div className="ms-auto d-flex align-items-center gap-2">
              {tabsRightSlot}
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={showOnlyCloneAction ? 'pe-none opacity-75' : undefined}
        aria-disabled={showOnlyCloneAction || undefined}
      >
      <LomavisCampaignBuilderSectionCard1
        id={sectionIds.campaign}
        title={t('adManager.sections.placements.title', 'Placements')}
        description={t(
          'adManager.sections.placements.description',
          'Choose where this campaign runs.'
        )}
        iconClass="fa-regular fa-bullhorn"
      >
        <div>
          <div className="mb-3">
            <Form.Label
              className="fw-bold small text-body"
              htmlFor="ad-manager-campaign-name"
            >
              {t('adManager.fields.campaignName', 'Campaign name')}
            </Form.Label>
            <Form.Control
              id="ad-manager-campaign-name"
              value={campaignName}
              maxLength={255}
              disabled={isActionBusy}
              onChange={(event) =>
                onCampaignNameChange?.(event.currentTarget.value)
              }
            />
          </div>
          <div className="mb-3">
            <Form.Label className="fw-bold small text-body">
              {t('adManager.fields.objective', 'Objective')}
            </Form.Label>
            <Form.Select
              value={objective}
              onChange={(event) => onObjectiveChange(event.currentTarget.value)}
            >
              {resolvedObjectiveOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="row row-cols-1 row-cols-xl-2 g-3">
            {Array.from(
              new Set(translatedPlacements.map((placement) => placement.platform))
            ).map((platform) => (
              <div className="col" key={platform}>
                <div className="vstack gap-3">
                  {translatedPlacements
                    .filter((placement) => placement.platform === platform)
                    .map((placement) => (
                      <LomavisPlacementOption1
                        key={placement.id}
                        id={placement.id}
                        label={placement.label}
                        platform={placement.platform}
                        selected={placement.selected}
                        onToggle={onPlacementToggle}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </LomavisCampaignBuilderSectionCard1>

      {showPixelTracking && (
        <LomavisCampaignBuilderSectionCard1
          title={t(
            'adManager.targeting.conversionTracking',
            'Conversion tracking'
          )}
          description={
            showConversionTracking
              ? t(
                  'adManager.targeting.conversionTrackingDescription',
                  'Choose the Meta pixel and event for this objective.'
                )
              : t(
                  'adManager.targeting.pixelTrackingDescription',
                  'Choose the Meta pixel for this objective.'
                )
          }
        >
          <div>
            <div className="row g-3 mt-1">
              <div
                className={
                  showConversionTracking ? 'col-12 col-lg-6' : 'col-12'
                }
              >
                <Form.Label className="fw-bold small text-body">
                  {t('adManager.fields.pixel', 'Pixel')}
                </Form.Label>
                <Form.Select
                  value={String(selectedPixelOption?.id || '')}
                  disabled={isLoadingPixels}
                  onChange={(event) => {
                    const nextValue = event.currentTarget.value;
                    if (!nextValue) {
                      onPixelChange(null);
                      return;
                    }
                    const selectedOption = pixelOptions.find(
                      (option) => String(option.id) === nextValue
                    );
                    onPixelChange(selectedOption || null);
                  }}
                >
                  <option value="">
                    {isLoadingPixels
                      ? t('adManager.pixels.loading', 'Loading pixels...')
                      : pixelOptions.length === 0
                      ? t('adManager.pixels.noneFound', 'No pixels found')
                      : t('adManager.pixels.select', 'Select pixel')}
                  </option>
                  {pixelOptions.map((option) => (
                    <option
                      key={String(option.id)}
                      value={String(option.id)}
                    >
                      {option.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
              {showConversionTracking && (
                <div className="col-12 col-lg-6">
                  <Form.Label className="fw-bold small text-body">
                    {t('adManager.fields.targetEvent', 'Target event')}
                  </Form.Label>
                  <Form.Select
                    value={String(targetEvent?.id || '')}
                    onChange={(event) => {
                      const selectedEvent = targetEventOptions.find(
                        (option) =>
                          String(option.id) === event.currentTarget.value
                      );
                      onTargetEventChange(selectedEvent || null);
                    }}
                  >
                    <option value="">
                      {t('adManager.targetEvents.select', 'Select event')}
                    </option>
                    {resolvedTargetEventOptions.map((option) => (
                      <option
                        key={String(option.id)}
                        value={String(option.id)}
                      >
                        {option.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )}
            </div>
          </div>
        </LomavisCampaignBuilderSectionCard1>
      )}

      <LomavisCampaignBuilderSectionCard1
        id={sectionIds.targeting}
        title={t('adManager.targeting.locations', 'Locations')}
        description={t(
          'adManager.targeting.locationsDescription',
          'Choose one targeting mode: whole countries or specific cities.'
        )}
        iconClass="fa-regular fa-bullseye"
      >
        <div className="row g-3 mt-1 align-items-stretch">
          <div className="col-12 col-md-6">
            <div
              className={`h-100 rounded-4 border p-3 p-md-4 ${
                locations.length > 0
                  ? 'bg-light border-2 opacity-75'
                  : 'bg-white border-2 border-primary-subtle'
              }`}
            >
              <div className="d-flex align-items-center gap-2 mb-1">
                <i className="fa-solid fa-earth-europe fs-5 text-primary" aria-hidden="true" />
                <span className="fw-bold text-body">
                  {t('adManager.targeting.countriesTitle', 'Target countries')}
                </span>
              </div>
              <p className="text-muted small mb-3">
                {t(
                  'adManager.targeting.countriesHelp',
                  'Reach everyone in one or more entire countries.'
                )}
              </p>
              <LomavisBadgeList1
                items={targetCountries.map((country) => ({
                  id: String(country.id),
                  title: country.name,
                  variant: 'primary'
                }))}
                maxVisible={6}
                onDelete={(id) =>
                  onTargetCountriesChange?.(
                    targetCountries.filter(
                      (country) => String(country.id) !== id
                    )
                  )
                }
                className="flex-wrap mb-3"
              />
              <div className={styles.typeaheadInputOnly}>
                <LomavisTypeaheadSelectInput1
                  id="lomavis-campaign-target-country-typeahead"
                  title={t(
                    'adManager.targeting.addAnotherCountry',
                    'Add another country'
                  )}
                  placeholder={t(
                    'adManager.targeting.searchCountry',
                    'Search for a country...'
                  )}
                  getOptions={() => targetCountryOptions}
                  initialValues={targetCountries}
                  minLength={1}
                  debounceMs={400}
                  flip
                  maxResults={100}
                  onInputChange={onTargetCountrySearch}
                  paginate={false}
                  positionFixed
                  disabled={locations.length > 0}
                  onSelectionChange={(newCountries) =>
                    onTargetCountriesChange?.(
                      newCountries as LomavisCampaignBuilderOption1[]
                    )
                  }
                />
                {locations.length > 0 && (
                  <small className="text-muted d-block mt-2">
                    <i className="fa-solid fa-circle-info me-1" aria-hidden="true" />
                    {t(
                      'adManager.targeting.countriesDisabledByCities',
                      'Remove selected cities to target countries.'
                    )}
                  </small>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div
              className={`h-100 rounded-4 border p-3 p-md-4 ${
                targetCountries.length > 0
                  ? 'bg-light border-2 opacity-75'
                  : 'bg-white border-2 border-primary-subtle'
              }`}
            >
              <div className="d-flex align-items-center gap-2 mb-1">
                <i
                  className="fa-solid fa-location-dot fs-5 text-primary"
                  aria-hidden="true"
                />
                <span className="fw-bold text-body">
                  {t('adManager.targeting.citiesTitle', 'Target cities')}
                </span>
              </div>
              <p className="text-muted small mb-3">
                {t(
                  'adManager.targeting.citiesHelp',
                  'Reach people around specific cities within a radius.'
                )}
              </p>
              <LomavisBadgeList1
                items={locations.map((location) => ({
                  id: location.id,
                  title: location.name,
                  variant: 'primary'
                }))}
                maxVisible={6}
                onDelete={(id) =>
                  onLocationsChange(
                    locations.filter((location) => location.id !== id)
                  )
                }
                className="flex-wrap mb-3"
              />
              <div className={styles.typeaheadInputOnly}>
                <LomavisTypeaheadSelectInput1
                  id="lomavis-campaign-location-typeahead"
                  title={t(
                    'adManager.targeting.addAnotherCity',
                    'Add another city'
                  )}
                  placeholder={t(
                    'adManager.targeting.searchLocation',
                    'Search for a city, region, or zip...'
                  )}
                  getOptions={() => locationOptions}
                  initialValues={locations}
                  minLength={2}
                  debounceMs={400}
                  flip
                  maxResults={100}
                  onInputChange={onLocationSearch}
                  paginate={false}
                  positionFixed
                  disabled={targetCountries.length > 0}
                  onSelectionChange={(newLocations) =>
                    onLocationsChange(
                      newLocations as LomavisCampaignBuilderLocation1[]
                    )
                  }
                />
                {targetCountries.length > 0 && (
                  <small className="text-muted d-block mt-2">
                    <i className="fa-solid fa-circle-info me-1" aria-hidden="true" />
                    {t(
                      'adManager.targeting.citiesDisabledByCountries',
                      'Remove selected countries to target cities.'
                    )}
                  </small>
                )}
              </div>
              {targetCountries.length === 0 && (
                <div className="mt-3">
                  <LomavisRadiusSelector1
                    valueKm={radiusKm}
                    minKm={radiusMinKm}
                    maxKm={radiusMaxKm}
                    onChange={onRadiusChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </LomavisCampaignBuilderSectionCard1>

      <LomavisCampaignBuilderSectionCard1
        title={t('adManager.targeting.demographics', 'Demographics')}
        description={t(
          'adManager.targeting.demographicsDescription',
          'Age, gender, and language.'
        )}
      >
        <div>
                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-6">
                    <Form.Label className="fw-bold small text-body">
                      {t('adManager.fields.ageRange', 'Age range')}
                    </Form.Label>
                    <div className="row g-3">
                      <div className="col-6">
                        <Form.Control
                          type="number"
                          min="13"
                          max="65"
                          disabled={hasRestrictedSpecialAdCategory}
                          value={ageRange[0]}
                          onChange={(event) =>
                            onAgeRangeChange([
                              Number(event.currentTarget.value),
                              ageRange[1]
                            ])
                          }
                        />
                      </div>
                      <div className="col-6">
                        <Form.Control
                          type="number"
                          min="13"
                          max="65"
                          disabled={hasRestrictedSpecialAdCategory}
                          value={ageRange[1]}
                          onChange={(event) =>
                            onAgeRangeChange([
                              ageRange[0],
                              Number(event.currentTarget.value)
                            ])
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <Form.Label className="fw-bold small text-body">
                      {t('adManager.fields.gender', 'Gender')}
                    </Form.Label>
                    <Form.Select
                      disabled={hasRestrictedSpecialAdCategory}
                      value={gender}
                      onChange={(event) =>
                        onGenderChange(event.currentTarget.value)
                      }
                    >
                      {resolvedGenderOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-6">
                    <Form.Label className="fw-bold small text-body">
                      {t(
                        'adManager.fields.specialAdCategory',
                        'Special ad category'
                      )}
                    </Form.Label>
                    <Form.Select
                      value={specialAdCategoryValue}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        onSpecialAdCategoriesChange?.(
                          nextValue === 'NONE' ? [] : [nextValue]
                        );
                      }}
                    >
                      {resolvedSpecialAdCategoryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className={`col-12 col-md-6 ${styles.typeaheadInputOnly}`}>
                    <LomavisTypeaheadSelectInput1
                      id="lomavis-campaign-language-typeahead"
                      title={t('adManager.targeting.languages', 'Languages')}
                      subtitle={t('common.optional', 'optional')}
                      placeholder={t(
                        'adManager.targeting.addLanguage',
                        'Add language'
                      )}
                      getOptions={() => languageOptions}
                      initialValues={languages}
                      minLength={1}
                      debounceMs={400}
                      flip
                      maxResults={100}
                      onInputChange={onLanguageSearch}
                      paginate={false}
                      positionFixed
                      showSelectedInInput
                      onSelectionChange={(newLanguages) =>
                        onLanguagesChange(newLanguages)
                      }
                    />
                  </div>
                </div>
                {hasSpecialAdCategory && (
                  <div className="row g-3 mt-1">
                    <div className="col-12 col-md-6">
                      <Form.Label className="fw-bold small text-body d-inline-flex align-items-center">
                        {t(
                          'adManager.fields.specialAdCategoryCountry',
                          'Legal jurisdiction'
                        )}
                        <LomavisMoreInfo
                          placement="top"
                          text={t(
                            'adManager.fields.specialAdCategoryCountryTooltip',
                            'Meta applies the anti-discrimination rules of these countries. Derived automatically from your targeting; falls back to your ad account country when no location is set. Not a targeting field.'
                          )}
                        />
                      </Form.Label>
                      <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
                        {specialAdCategoryCountry.length > 0 ? (
                          specialAdCategoryCountry.map((code) => (
                            <LomavisBadge1
                              key={code}
                              id={code}
                              variant="secondary"
                              title={code}
                            />
                          ))
                        ) : (
                          <span className="text-muted small">
                            {t(
                              'adManager.specialAdCategoryCountry.empty',
                              'Add a target country or city to set the jurisdiction.'
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
        </div>
      </LomavisCampaignBuilderSectionCard1>

      <LomavisCampaignBuilderSectionCard1
        title={t(
          'adManager.targeting.interestsAndBehaviors',
          'Interests and behaviors'
        )}
        description={`${interests.length} ${t(
          'adManager.targeting.interestsSelected',
          'interests'
        )} - ${behaviors.length} ${t(
          'adManager.targeting.behaviorsSelected',
          'behaviors selected'
        )}`}
      >
        <div>
                <div className="mt-3">
                  <LomavisBadgeList1
                    items={toBadgeItems(interests)}
                    maxVisible={12}
                    onDelete={(id) =>
                      onInterestsChange(
                        interests.filter(
                          (interest) => String(interest.id) !== id
                        )
                      )
                    }
                    className="flex-wrap"
                  />
                </div>
                <div className="mt-3">
                  <LomavisBadgeList1
                    items={behaviors.map((behavior) => ({
                      id: String(behavior.id),
                      title: behavior.name,
                      color: '#e78128'
                    }))}
                    maxVisible={6}
                    onDelete={onBehaviorRemove}
                    className="flex-wrap"
                  />
                </div>
                <div className={`${styles.typeaheadInputOnly} mt-3`}>
                  <LomavisTypeaheadSelectInput1
                    id="lomavis-campaign-interest-typeahead"
                    title={t('adManager.targeting.addInterest', 'Add interest')}
                    placeholder={t(
                      'adManager.targeting.searchInterests',
                      'Search interests...'
                    )}
                    getOptions={() => interestOptions}
                    initialValues={interests}
                    minLength={2}
                    debounceMs={400}
                    flip
                    maxResults={100}
                    onInputChange={onInterestSearch}
                    paginate={false}
                    positionFixed
                    disabled={hasRestrictedSpecialAdCategory}
                    onSelectionChange={(newInterests) =>
                      onInterestsChange(newInterests)
                    }
                  />
                </div>
                {interestsSectionSlot ? (
                  <div className="mt-3">{interestsSectionSlot}</div>
                ) : null}
        </div>
      </LomavisCampaignBuilderSectionCard1>

      {onCustomAudiencesChange ? (
        <LomavisCampaignBuilderSectionCard1
          title={t(
            'adManager.targeting.customAudiencesTitle',
            'Custom audiences from Meta'
          )}
          description={t(
            'adManager.targeting.customAudiencesDescription',
            'Reuse audiences you created in Meta Ads Manager (customer lists, website visitors, engagement).'
          )}
        >
          <div>
            {customAudiencesError ? (
              <div className="alert alert-warning d-flex align-items-center justify-content-between gap-2 mb-3">
                <span>{customAudiencesError}</span>
                {onRetryCustomAudiences ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    onClick={onRetryCustomAudiences}
                  >
                    {t('common.retry', 'Retry')}
                  </Button>
                ) : null}
              </div>
            ) : null}
            <div className="mt-3">
              <LomavisBadgeList1
                items={customAudiences.map((audience) => ({
                  id: String(audience.id),
                  title: audience.name,
                  variant: 'primary'
                }))}
                maxVisible={12}
                onDelete={(id) =>
                  onCustomAudiencesChange(
                    customAudiences.filter(
                      (audience) => String(audience.id) !== id
                    )
                  )
                }
                className="flex-wrap"
              />
            </div>
            <div className={`${styles.typeaheadInputOnly} mt-3`}>
              <LomavisTypeaheadSelectInput1
                id="lomavis-campaign-custom-audience-typeahead"
                title={t(
                  'adManager.targeting.addCustomAudience',
                  'Add custom audience'
                )}
                placeholder={
                  isLoadingCustomAudiences
                    ? t(
                        'adManager.targeting.customAudiencesLoading',
                        'Loading custom audiences...'
                      )
                    : t(
                        'adManager.targeting.searchCustomAudiences',
                        'Search custom audiences...'
                      )
                }
                getOptions={() => customAudienceOptions}
                initialValues={customAudiences}
                minLength={0}
                debounceMs={0}
                flip
                maxResults={100}
                paginate={false}
                positionFixed
                disabled={isLoadingCustomAudiences}
                onSelectionChange={(nextCustomAudiences) =>
                  onCustomAudiencesChange(nextCustomAudiences)
                }
              />
            </div>
          </div>
        </LomavisCampaignBuilderSectionCard1>
      ) : null}

      <LomavisCampaignBuilderSectionCard1
        id={sectionIds.creative}
        title={t(
          'adManager.sections.creative.title',
          'Creative and live preview'
        )}
        description={t(
          'adManager.sections.creative.description',
          'Drop your media on the canvas. Switch placements to see how the ad renders on each network.'
        )}
        iconClass="fa-regular fa-palette"
      >
        <div className="row g-3">
          <div className="col-12 col-xl-8">
            <div className="vstack gap-3">
              <LomavisMediaUploadPanel1
                selectedFiles={selectedMediaFiles}
                onBrowse={onBrowseMedia}
                onOpenMediaLibrary={onOpenMediaLibrary}
                onFilesDrop={onMediaFilesDrop}
                onFileRemove={onFileRemove}
                onCancelUpload={onCancelUpload}
                uploadProgress={uploadProgress}
                notice={mediaValidationNotice}
                disabled={isUploadingMedia || mediaUploadDisabled}
              />
              <div className="border rounded-3 bg-white p-3">
                <LomavisCampaignSectionHeader1
                  title={t('adManager.creative.adCopy', 'Ad copy')}
                  description={t(
                    'adManager.creative.adCopyDescription',
                    'Primary text, CTA, and landing URL.'
                  )}
                />
                <div className="vstack gap-3 mt-3">
                  <div>
                    <Form.Label className="fw-bold small text-body">
                      {t('adManager.fields.primaryText', 'Primary text')}
                    </Form.Label>
                    <LomavisCampaignPrimaryTextEditor1
                      onChange={(primaryText) =>
                        onAdChange({
                          ...ad,
                          primaryText
                        })
                      }
                      value={ad.primaryText}
                    />
                  </div>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <Form.Label className="fw-bold small text-body">
                        {t('adManager.fields.cta', 'CTA')}
                      </Form.Label>
                      <Form.Select
                        value={ad.cta}
                        onChange={(event) =>
                          onAdChange({
                            ...ad,
                            cta: event.currentTarget.value
                          })
                        }
                      >
                        {resolvedCtaOptions.map((ctaOption) => (
                          <option key={ctaOption.value} value={ctaOption.value}>
                            {ctaOption.label}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div className="col-12 col-md-6">
                      <Form.Label className="fw-bold small text-body">
                        {t('adManager.fields.landingUrl', 'Landing URL')}
                      </Form.Label>
                      <Form.Control
                        value={ad.landingUrl}
                        onChange={(event) =>
                          onAdChange({
                            ...ad,
                            landingUrl: event.currentTarget.value
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-4">
            <LomavisCampaignLivePreview1
              placements={previewPlacements}
              activePlacementKey={activePreviewPlacementKey}
              onPlacementChange={onPreviewPlacementChange}
              ad={previewAd}
              mediaFiles={selectedMediaFiles}
            />
          </div>
        </div>
      </LomavisCampaignBuilderSectionCard1>

      <LomavisCampaignBuilderSectionCard1
        id={sectionIds.budget}
        title={t('adManager.sections.budget.title', 'Budget and schedule')}
        description={t(
          'adManager.sections.budget.description',
          'Set how much to spend and when the campaign runs.'
        )}
        iconClass="fa-regular fa-wallet"
      >
        <div className="row g-3">
          <div className="col-12">
            <div className="border rounded-3 bg-white p-3">
              <LomavisBudgetControls1
                budgetType={budgetType}
                onBudgetTypeChange={onBudgetTypeChange}
                amount={amount}
                onAmountChange={onAmountChange}
                startDate={startDate}
                onStartDateChange={onStartDateChange}
                endDate={endDate}
                onEndDateChange={onEndDateChange}
                bidStrategy={bidStrategy}
                onBidStrategyChange={onBidStrategyChange}
                maxBidAmount={maxBidAmount}
                onMaxBidAmountChange={onMaxBidAmountChange}
                currency={currency}
                estimatedTotalLabel={estimatedTotalLabel}
                estimatedPeriodLabel={estimatedPeriodLabel}
              />
              <div className="row g-3 mt-2">
                <div className="col-12 col-md-6">
                  <label
                    className="form-label fw-semibold"
                    htmlFor="ad-manager-dsa-beneficiary"
                  >
                    {t('adManager.fields.dsaBeneficiary', 'Beneficiary')}
                  </label>
                  <input
                    id="ad-manager-dsa-beneficiary"
                    type="text"
                    className="form-control"
                    maxLength={DSA_ENTITY_MAX_LENGTH}
                    aria-describedby={dsaBeneficiaryLengthHelpId}
                    value={dsaBeneficiary}
                    onChange={(event) =>
                      onDsaBeneficiaryChange(event.target.value)
                    }
                  />
                  <Form.Text
                    id={dsaBeneficiaryLengthHelpId}
                    className="d-block text-end text-muted"
                  >
                    {dsaBeneficiary.length}/{DSA_ENTITY_MAX_LENGTH}
                  </Form.Text>
                </div>
                <div className="col-12 col-md-6">
                  <label
                    className="form-label fw-semibold"
                    htmlFor="ad-manager-dsa-payor"
                  >
                    {t('adManager.fields.dsaPayor', 'Person who pays')}
                  </label>
                  <input
                    id="ad-manager-dsa-payor"
                    type="text"
                    className="form-control"
                    maxLength={DSA_ENTITY_MAX_LENGTH}
                    aria-describedby={dsaPayorLengthHelpId}
                    value={dsaPayor}
                    onChange={(event) => onDsaPayorChange(event.target.value)}
                  />
                  <Form.Text
                    id={dsaPayorLengthHelpId}
                    className="d-block text-end text-muted"
                  >
                    {dsaPayor.length}/{DSA_ENTITY_MAX_LENGTH}
                  </Form.Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LomavisCampaignBuilderSectionCard1>
      </div>

      <section className={`${styles.section} bg-light`}>
        <div className="container-xxl px-4 py-4">
          <div
            ref={actionFooterRef}
            className="rounded-4 border bg-white p-3 p-md-4 d-flex flex-column gap-3"
          >
            <LomavisIssueListAlert1 issues={resolvedActionValidationIssues} />
            <div className="d-flex flex-wrap align-items-center gap-2">
              <div className="border rounded-3 bg-white px-3 py-2">
                <div className="text-muted small fw-bold text-uppercase">
                  {t('adManager.summary.audience', 'Audience')}
                </div>
                <div className="fw-bold text-body">
                  {audienceEstimate} {t('adManager.people', 'people')}
                </div>
              </div>
              <div className="border rounded-3 bg-white px-3 py-2">
                <div className="text-muted small fw-bold text-uppercase">
                  {t('adManager.budget.estimatedTotal', 'Estimated total')}
                </div>
                <div className="fw-bold text-body">
                  {estimatedTotalLabel} {estimatedPeriodLabel}
                </div>
              </div>
            </div>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              {onCancel ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={
                    isActionBusy || isUploadingMedia || cancelActionDisabled
                  }
                  onClick={onCancel}
                >
                  {resolvedCancelActionLabel}
                </Button>
              ) : (
                <span />
              )}
              <div className="d-flex flex-wrap align-items-center gap-2">
                {showOnlyCloneAction ? (
                  onClone ? (
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      disabled={isActionBusy || cloneActionDisabled}
                      onClick={onClone}
                    >
                      {resolvedCloneActionLabel}
                    </Button>
                  ) : null
                ) : isPublishing ? (
                  <div
                    className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 bg-primary-subtle text-primary fw-semibold"
                    role="status"
                    aria-live="polite"
                  >
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      aria-hidden="true"
                    />
                    <span>{t('adManager.publishing', 'Publishing...')}</span>
                  </div>
                ) : (
                  <>
                    {handleSave && (
                      <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        disabled={isActionBusy || resolvedSaveDisabled}
                        onClick={handleSave}
                      >
                        {isSaving
                          ? t('adManager.saving', 'Saving...')
                          : resolvedSaveLabel}
                      </Button>
                    )}
                    {showStandaloneSaveAsDraft && onSaveAsDraft && (
                      <Button
                        type="button"
                        variant="warning"
                        size="sm"
                        onClick={onSaveAsDraft}
                      >
                        {isSaving
                          ? t('adManager.saving', 'Saving...')
                          : resolvedSaveAsDraftActionLabel}
                      </Button>
                    )}
                    {!hidePrimaryAction &&
                      (tailActions.length > 0 ? (
                        <LomavisCampaignActionDropdown1
                          items={tailActions}
                          isBusy={isActionBusy}
                          drop="up"
                          primaryVariant="primary"
                          popperStrategy="absolute"
                          primaryAction={{
                            label: resolvedPrimaryActionLabel,
                            disabled: primaryActionDisabled,
                            onClick: onPublish
                          }}
                          ariaLabel={t(
                            'adManager.publishActionsToggle',
                            'Toggle publish actions'
                          )}
                        />
                      ) : (
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          disabled={isActionBusy || primaryActionDisabled}
                          onClick={onPublish}
                        >
                          {resolvedPrimaryActionLabel}
                        </Button>
                      ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
